import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Autocomplete,
  TextField,
  Stack
} from '@mui/material';
import {
  CompareArrows,
  Add,
  Close,
  Map as MapIcon
} from '@mui/icons-material';
import StationPickerDialog from './StationPickerDialog';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { compareService, stationService } from '../services/api';
import { SUPPORTED_METRICS } from '../config/sensorMetrics';

interface Station {
  id?: number;       // Numeric ID from API (for public stations)
  uuid?: string;     // UUID (for owned stations)
  name: string;
  status: string;
  is_owner?: boolean;
  available_metrics?: string[];  // List of metrics that have data
  location?: {
    latitude: number;
    longitude: number;
  };
}

interface ComparisonDataPoint {
  timestamp: string;
  avg: number | null;
}

interface StationComparisonData {
  id: string;
  name: string;
  color: string;
  data: ComparisonDataPoint[];
  data_points: number;
}

interface ComparisonResponse {
  metric: string;
  metric_label: string;
  metric_unit: string;
  aggregation: string;
  period_hours: number;
  stations: StationComparisonData[];
  warnings: Array<{ station_id: string; station_name?: string; message: string }>;
}

interface StationCompareProps {
  stations: Station[];
}

// Available metrics for comparison (using internal names for API)
const COMPARE_METRICS = SUPPORTED_METRICS.map(m => ({
  value: m.internal === 'humidity' ? 'rh_percentage' : m.internal,
  label: m.userFriendly.charAt(0).toUpperCase() + m.userFriendly.slice(1).replace(/_/g, ' '),
  unit: m.unit,
  color: m.color
}));

const TIME_RANGES = [
  { value: 24, label: '24 Hours' },
  { value: 48, label: '48 Hours' },
  { value: 72, label: '3 Days' },
  { value: 168, label: '1 Week' },
  { value: 336, label: '2 Weeks' },
  { value: 720, label: '30 Days' }
];

const StationCompare: React.FC<StationCompareProps> = ({ stations }) => {
  const [allStations, setAllStations] = useState<Station[]>(stations);
  const [selectedStations, setSelectedStations] = useState<Station[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<string>('temperature_celsius');
  const [timeRange, setTimeRange] = useState<number>(24);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [comparisonData, setComparisonData] = useState<ComparisonResponse | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [autocompleteKey, setAutocompleteKey] = useState<number>(0);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);

  // Fetch owned + public stations for comparison
  useEffect(() => {
    const fetchAllStations = async () => {
      try {
        const data = await stationService.getAll({ include_public: true, limit: 500 });
        setAllStations(data);
      } catch (err) {
        console.error('Failed to fetch stations for compare:', err);
        setAllStations(stations); // fallback to owned-only
      }
    };
    fetchAllStations();
  }, [stations]);

  // Helper to get station identifier (id or uuid)
  const getStationId = (station: Station): string => {
    return station.uuid || String(station.id) || '';
  };

  // Map frontend metric names to backend field names
  const getBackendMetricName = (metric: string): string => {
    if (metric === 'humidity') return 'rh_percentage';
    return metric;
  };

  // Filter active stations that have data for the selected metric
  const availableStations = allStations.filter(s => {
    // Must be active
    if (s.status !== 'active') return false;
    // Must not be already selected
    if (selectedStations.find(sel => getStationId(sel) === getStationId(s))) return false;
    // Must have data for the selected metric
    const backendMetric = getBackendMetricName(selectedMetric);
    if (s.available_metrics && !s.available_metrics.includes(backendMetric)) return false;
    return true;
  });

  const handleAddStation = (station: Station | null) => {
    if (station && selectedStations.length < 5) {
      setSelectedStations([...selectedStations, station]);
      // Reset autocomplete by changing its key
      setAutocompleteKey(prev => prev + 1);
    }
  };

  const handleRemoveStation = (stationId: string) => {
    setSelectedStations(selectedStations.filter(s => getStationId(s) !== stationId));
  };

  // Handle metric change - clear selected stations that don't have this metric
  const handleMetricChange = (newMetric: string) => {
    const backendMetric = getBackendMetricName(newMetric);
    // Filter out stations that don't have data for the new metric
    const validStations = selectedStations.filter(s =>
      s.available_metrics && s.available_metrics.includes(backendMetric)
    );
    setSelectedMetric(newMetric);
    setSelectedStations(validStations);
    setAutocompleteKey(prev => prev + 1);
  };

  const fetchComparisonData = async () => {
    if (selectedStations.length < 2) {
      setError('Select at least 2 stations to compare');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await compareService.getComparison({
        stationIds: selectedStations.map(s => getStationId(s)),
        metric: selectedMetric,
        hours: timeRange
      });

      setComparisonData(response);

      // Transform data for Recharts - merge all stations' data by timestamp
      const timestampMap = new Map<string, any>();

      response.stations.forEach((station: StationComparisonData) => {
        station.data.forEach((point: ComparisonDataPoint) => {
          const existing = timestampMap.get(point.timestamp) || {
            timestamp: point.timestamp,
            timestampMs: new Date(point.timestamp).getTime()
          };
          existing[station.name] = point.avg;
          timestampMap.set(point.timestamp, existing);
        });
      });

      // Sort by timestamp and convert to array
      const sortedData = Array.from(timestampMap.values()).sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      setChartData(sortedData);

      // Show warnings if any
      if (response.warnings && response.warnings.length > 0) {
        const warningMessages = response.warnings.map((w: any) =>
          w.station_name ? `${w.station_name}: ${w.message}` : w.message
        ).join('; ');
        setError(`Warning: ${warningMessages}`);
      }
    } catch (err: any) {
      console.error('Comparison error:', err);
      setError(err.response?.data?.error || 'Failed to fetch comparison data');
      setComparisonData(null);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch when selections change and we have enough stations
  useEffect(() => {
    if (selectedStations.length < 2) {
      setComparisonData(null);
      setChartData([]);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await compareService.getComparison({
          stationIds: selectedStations.map(s => getStationId(s)),
          metric: selectedMetric,
          hours: timeRange
        });

        if (cancelled) return;

        setComparisonData(response);

        const timestampMap = new Map<string, any>();
        response.stations.forEach((station: StationComparisonData) => {
          station.data.forEach((point: ComparisonDataPoint) => {
            const existing = timestampMap.get(point.timestamp) || {
              timestamp: point.timestamp,
              timestampMs: new Date(point.timestamp).getTime()
            };
            existing[station.name] = point.avg;
            timestampMap.set(point.timestamp, existing);
          });
        });

        const sortedData = Array.from(timestampMap.values()).sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        setChartData(sortedData);

        if (response.warnings && response.warnings.length > 0) {
          const warningMessages = response.warnings.map((w: any) =>
            w.station_name ? `${w.station_name}: ${w.message}` : w.message
          ).join('; ');
          setError(`Warning: ${warningMessages}`);
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error('Comparison error:', err);
        setError(err.response?.data?.error || 'Failed to fetch comparison data');
        setComparisonData(null);
        setChartData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => { cancelled = true; };
  }, [selectedStations, selectedMetric, timeRange]);

  const currentMetricConfig = COMPARE_METRICS.find(m => m.value === selectedMetric);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CompareArrows sx={{ fontSize: 32, mr: 2, color: '#00d4ff' }} />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Compare Stations
        </Typography>
      </Box>

      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mb: 2, display: 'block' }}>
        All values are displayed in standard units (SI) to ensure accurate comparison across stations.
      </Typography>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: 'rgba(255,255,255,0.02)' }}>
        <Stack spacing={3}>
          {/* Step 1: Metric and Time Range Selection */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              1. Select Metric and Time Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Metric</InputLabel>
                <Select
                  value={selectedMetric}
                  label="Metric"
                  onChange={(e) => handleMetricChange(e.target.value)}
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      style: { maxHeight: 300 }
                    }
                  }}
                >
                  {COMPARE_METRICS.map((metric) => (
                    <MenuItem key={metric.value} value={metric.value}>
                      {metric.label} ({metric.unit})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  label="Time Range"
                  onChange={(e) => setTimeRange(e.target.value as number)}
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      style: { maxHeight: 300 }
                    }
                  }}
                >
                  {TIME_RANGES.map((range) => (
                    <MenuItem key={range.value} value={range.value}>
                      {range.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Step 2: Station Selection */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              2. Select Stations ({selectedStations.length}/5) - Only stations with {currentMetricConfig?.label || selectedMetric} data
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {selectedStations.map((station) => (
                <Chip
                  key={getStationId(station)}
                  label={station.name}
                  onDelete={() => handleRemoveStation(getStationId(station))}
                  deleteIcon={<Close />}
                  sx={{
                    backgroundColor: 'rgba(0, 212, 255, 0.1)',
                    borderColor: 'rgba(0, 212, 255, 0.3)',
                    '& .MuiChip-deleteIcon': {
                      color: 'rgba(255,255,255,0.7)',
                      '&:hover': { color: '#ff5252' }
                    }
                  }}
                  variant="outlined"
                />
              ))}
              {selectedStations.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No stations selected. Add at least 2 stations to compare.
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {selectedStations.length < 5 && (
                <Autocomplete
                  key={autocompleteKey}
                  options={availableStations}
                  getOptionLabel={(option) => option.name}
                  onChange={(_, value) => handleAddStation(value)}
                  noOptionsText={`No stations with ${currentMetricConfig?.label || selectedMetric} data available`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Station"
                      placeholder="Search stations..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <Add sx={{ color: 'text.secondary', mr: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  sx={{ minWidth: 300 }}
                />
              )}

            <Button
                variant="outlined"
                size="small"
                onClick={() => setMapPickerOpen(true)}
                disabled={selectedStations.length >= 5}
                startIcon={<MapIcon />}
                sx={{
                  borderColor: 'rgba(0, 212, 255, 0.5)',
                  color: '#00d4ff',
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.8rem',
                  '&:hover': {
                    borderColor: '#00d4ff',
                    backgroundColor: 'rgba(0, 212, 255, 0.1)'
                  }
                }}
              >
                Pick from Map
              </Button>

            <Button
                variant="outlined"
                size="small"
                onClick={fetchComparisonData}
                disabled={selectedStations.length < 2 || loading}
                sx={{
                  borderColor: 'rgba(0, 212, 255, 0.5)',
                  color: '#00d4ff',
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.8rem',
                  '&:hover': {
                    borderColor: '#00d4ff',
                    backgroundColor: 'rgba(0, 212, 255, 0.1)'
                  }
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </Stack>
      </Paper>

      {/* Error/Warning Display */}
      {error && (
        <Alert
          severity={error.startsWith('Warning') ? 'warning' : 'error'}
          sx={{ mb: 3 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Chart */}
      <Paper sx={{ p: 3, backgroundColor: 'rgba(255,255,255,0.02)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress sx={{ color: '#00d4ff' }} />
          </Box>
        ) : chartData.length > 0 && comparisonData ? (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {comparisonData.metric_label} Comparison (Hourly Average)
            </Typography>
            <ResponsiveContainer width="100%" height={450}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis
                  dataKey="timestampMs"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  stroke="rgba(255,255,255,0.5)"
                  tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }}
                  tickFormatter={(ts) => new Date(ts).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.5)"
                  tick={{ fill: 'rgba(255,255,255,0.7)' }}
                  label={{
                    value: comparisonData.metric_unit,
                    angle: -90,
                    position: 'insideLeft',
                    fill: 'rgba(255,255,255,0.7)'
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(30, 30, 30, 0.95)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8
                  }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value: number) => [
                    value !== null ? `${value.toFixed(2)} ${comparisonData.metric_unit}` : 'N/A',
                  ]}
                />
                <Legend
                  wrapperStyle={{ paddingTop: 20 }}
                  formatter={(value) => <span style={{ color: 'rgba(255,255,255,0.8)' }}>{value}</span>}
                />
                {comparisonData.stations.map((station) => (
                  <Line
                    key={station.id}
                    type="monotone"
                    dataKey={station.name}
                    stroke={station.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>

            {/* Data Summary */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {comparisonData.stations.map((station) => (
                <Chip
                  key={station.id}
                  label={`${station.name}: ${station.data_points} data points`}
                  size="small"
                  sx={{
                    backgroundColor: station.color,
                    color: '#fff',
                    opacity: 0.9
                  }}
                />
              ))}
            </Box>
          </>
        ) : (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: 400,
            color: 'text.secondary'
          }}>
            <CompareArrows sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Select Stations to Compare
            </Typography>
            <Typography variant="body2">
              Choose at least 2 stations (max 5) and a metric to see the comparison chart
            </Typography>
          </Box>
        )}
      </Paper>

      <StationPickerDialog
        open={mapPickerOpen}
        onClose={() => setMapPickerOpen(false)}
        onSelect={handleAddStation}
        stations={availableStations}
        selectedStationIds={selectedStations.map(s => getStationId(s))}
      />
    </Box>
  );
};

export default StationCompare;
